import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
    Check,
    Close,
    People,
    CalendarMonth,
    Payment,
    PhoneIphone,
    LocalOffer,
    CardGiftcard,
    FitnessCenter,
    EmojiEvents,
    ArrowBack,
} from '@mui/icons-material';
import { AppContext } from '../../../sporttia/AppContext';
import { getPath } from '../../Pages';

type PlanFeature = {
    text: string;
    included: boolean;
    icon: React.ReactElement;
};

type Plan = {
    id: string;
    name: string;
    price: string;
    priceSubtext?: string;
    features: PlanFeature[];
    highlighted?: boolean;
    buttonText: string;
    buttonAction: () => void;
};

export default function Plans(): React.ReactElement {
    const navigate = useNavigate();
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    useEffect(() => {
        cxt?.setHeader({
            title: t('plans.title'),
        });
    }, []);

    const handleContactSporttia = (): void => {
        window.open('https://sporttia.com/contact', '_blank');
    };

    const baseFeatures: PlanFeature[] = [
        {
            text: t('plans.features.manageUsers'),
            included: true,
            icon: <People fontSize="small" />,
        },
        {
            text: t('plans.features.manageBookings'),
            included: true,
            icon: <CalendarMonth fontSize="small" />,
        },
        {
            text: t('plans.features.managePayments'),
            included: true,
            icon: <Payment fontSize="small" />,
        },
        {
            text: t('plans.features.freeApp'),
            included: true,
            icon: <PhoneIphone fontSize="small" />,
        },
        {
            text: t('plans.features.manageDiscounts'),
            included: true,
            icon: <LocalOffer fontSize="small" />,
        },
        {
            text: t('plans.features.manageVouchers'),
            included: true,
            icon: <CardGiftcard fontSize="small" />,
        },
    ];

    const plans: Plan[] = [
        {
            id: 'free',
            name: t('plans.free.name'),
            price: t('plans.free.price'),
            features: [
                ...baseFeatures,
                {
                    text: t('plans.features.maxUsers', { count: 50 }),
                    included: true,
                    icon: <People fontSize="small" />,
                },
                {
                    text: t('plans.features.maxFacilities', { count: 4 }),
                    included: true,
                    icon: <FitnessCenter fontSize="small" />,
                },
                {
                    text: t('plans.features.activities'),
                    included: false,
                    icon: <FitnessCenter fontSize="small" />,
                },
                {
                    text: t('plans.features.events'),
                    included: false,
                    icon: <EmojiEvents fontSize="small" />,
                },
            ],
            buttonText: t('plans.currentPlan'),
            buttonAction: () => {},
        },
        {
            id: 'basic',
            name: t('plans.basic.name'),
            price: '18€',
            priceSubtext: t('plans.perMonth'),
            highlighted: true,
            features: [
                ...baseFeatures,
                {
                    text: t('plans.features.unlimitedUsers'),
                    included: true,
                    icon: <People fontSize="small" />,
                },
                {
                    text: t('plans.features.unlimitedFacilities'),
                    included: true,
                    icon: <FitnessCenter fontSize="small" />,
                },
                {
                    text: t('plans.features.activities'),
                    included: false,
                    icon: <FitnessCenter fontSize="small" />,
                },
                {
                    text: t('plans.features.events'),
                    included: false,
                    icon: <EmojiEvents fontSize="small" />,
                },
            ],
            buttonText: t('plans.choosePlan'),
            buttonAction: handleContactSporttia,
        },
        {
            id: 'pro',
            name: t('plans.pro.name'),
            price: t('plans.pro.price'),
            features: [
                ...baseFeatures,
                {
                    text: t('plans.features.unlimitedUsers'),
                    included: true,
                    icon: <People fontSize="small" />,
                },
                {
                    text: t('plans.features.unlimitedFacilities'),
                    included: true,
                    icon: <FitnessCenter fontSize="small" />,
                },
                {
                    text: t('plans.features.activities'),
                    included: true,
                    icon: <FitnessCenter fontSize="small" />,
                },
                {
                    text: t('plans.features.events'),
                    included: true,
                    icon: <EmojiEvents fontSize="small" />,
                },
            ],
            buttonText: t('plans.contactUs'),
            buttonAction: handleContactSporttia,
        },
    ];

    return (
        <Box>
            {/* Back button */}
            <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate(getPath('subscriptions'))}
                sx={{ mb: 3 }}
            >
                {t('plans.backToSubscription')}
            </Button>

            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                    {t('plans.chooseYourPlan')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {t('plans.choosePlanSubtitle')}
                </Typography>
            </Box>

            {/* Plans Grid */}
            <Grid container spacing={3} justifyContent="center">
                {plans.map((plan) => (
                    <Grid size={{ xs: 12, md: 4 }} key={plan.id}>
                        <Card
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative',
                                border: plan.highlighted
                                    ? '2px solid'
                                    : '1px solid',
                                borderColor: plan.highlighted
                                    ? 'primary.dark'
                                    : 'divider',
                                boxShadow: plan.highlighted ? 4 : 1,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 6,
                                },
                            }}
                        >
                            {plan.highlighted && (
                                <Chip
                                    label={t('plans.recommended')}
                                    color="primary"
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        fontWeight: 600,
                                        zIndex: 1,
                                    }}
                                />
                            )}

                            <CardContent
                                sx={{
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    p: 4,
                                    pt: 5,
                                }}
                            >
                                {/* Plan Name */}
                                <Typography
                                    variant="h5"
                                    fontWeight={700}
                                    textAlign="center"
                                    gutterBottom
                                >
                                    {plan.name}
                                </Typography>

                                {/* Price */}
                                <Box sx={{ textAlign: 'center', mb: 4 }}>
                                    <Typography
                                        variant="h3"
                                        fontWeight={700}
                                        color={
                                            plan.highlighted
                                                ? 'var(--brand-text)'
                                                : 'text.primary'
                                        }
                                    >
                                        {plan.price}
                                    </Typography>
                                    {plan.priceSubtext && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.5 }}
                                        >
                                            {plan.priceSubtext}
                                        </Typography>
                                    )}
                                </Box>

                                <Divider sx={{ mb: 4 }} />

                                {/* Features */}
                                <Box sx={{ flexGrow: 1, py: 2 }}>
                                    {plan.features.map((feature, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                mb: 2.5,
                                                opacity: feature.included
                                                    ? 1
                                                    : 0.5,
                                            }}
                                        >
                                            {feature.included ? (
                                                <Check
                                                    fontSize="small"
                                                    color="success"
                                                />
                                            ) : (
                                                <Close
                                                    fontSize="small"
                                                    color="disabled"
                                                />
                                            )}
                                            <Box
                                                sx={{
                                                    color: 'text.secondary',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                {feature.icon}
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    textDecoration:
                                                        feature.included
                                                            ? 'none'
                                                            : 'line-through',
                                                }}
                                            >
                                                {feature.text}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>

                                {/* Action Button */}
                                <Button
                                    variant={
                                        plan.highlighted
                                            ? 'contained'
                                            : 'outlined'
                                    }
                                    color="primary"
                                    fullWidth
                                    size="large"
                                    onClick={plan.buttonAction}
                                    disabled={plan.id === 'free'}
                                    sx={{
                                        mt: 4,
                                        py: 2,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                    }}
                                >
                                    {plan.buttonText}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Footer note */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="body2" color="text.secondary">
                    {t('plans.footerNote')}
                </Typography>
            </Box>
        </Box>
    );
}
